import { useCallback, useMemo } from "react";

import {
  ErrorResponse,
  getErrorMessages,
  useGetUserDetailsQuery,
} from "../../../services";
import { UseSettingsDataResult } from "./types";

const FALLBACK_ERROR =
  "An unexpected error occurred while fetching user details.";

/**
 * Reads `/auth/details` and splits the single payload into the two shapes the
 * Settings page renders: the profile fields and the preference toggles.
 */
export const useSettingsData = (): UseSettingsDataResult => {
  const { data, isLoading, error, refetch } = useGetUserDetailsQuery();

  const userDetails = useMemo(() => {
    if (!data) {
      return null;
    }
    return {
      firstName: data.firstName,
      lastName: data.lastName,
      email: data.email,
      phone: data.phone,
      isVerified: data.isVerified,
      isActive: data.isActive,
      dateOfBirth: data.dateOfBirth ? new Date(data.dateOfBirth) : null,
      photo: data.photo,
    };
  }, [data]);

  const userPreferences = useMemo(() => {
    if (!data) {
      return null;
    }
    return {
      emailNotifications: data.emailNotifications,
      newsletterConsent: data.newsletterConsent,
      darkMode: data.darkMode,
      language: data.language,
    };
  }, [data]);

  const errorMessage = error
    ? getErrorMessages(error as ErrorResponse)[0] || FALLBACK_ERROR
    : null;

  const retry = useCallback(async () => {
    await refetch();
  }, [refetch]);

  return {
    userDetails,
    userPreferences,
    isLoading,
    error: errorMessage,
    refetch: retry,
  };
};
